import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MessageCircle, ArrowDown } from "lucide-react";
import { MaskLines } from "./anim";
import { WHATSAPP } from "./data";
import { useBookCall } from "./BookCall";

export const Hero = () => {
  const ref = useRef(null);
  const { openBookCall } = useBookCall();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yWord = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yImgA = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const yImgB = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden px-6 pb-20 pt-36 md:px-10"
    >
      {/* animated spotlight gradients */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -left-40 top-10 h-[520px] w-[520px] rounded-full blur-[130px]"
        style={{ background: "hsl(var(--glow) / 0.28)" }}
        animate={{ scale: [1, 1.25, 1], opacity: [0.6, 0.95, 0.6] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full blur-[120px]"
        style={{ background: "hsl(var(--glow) / 0.16)" }}
        animate={{ scale: [1.1, 1, 1.1] }}
        transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* floating 3D abstract imagery */}
      <motion.img
        src="https://images.unsplash.com/photo-1622737133809-d95047b9e673?crop=entropy&cs=srgb&fm=jpg&q=85&w=700"
        alt=""
        aria-hidden
        style={{ y: yImgA }}
        className="float-slow pointer-events-none absolute right-[6%] top-[16%] hidden w-44 rotate-6 rounded-2xl border border-[hsl(var(--border))] object-cover opacity-90 shadow-2xl lg:block xl:w-56"
      />
      <motion.img
        src="https://images.unsplash.com/photo-1627637819794-fba32f82be16?crop=entropy&cs=srgb&fm=jpg&q=85&w=700"
        alt=""
        aria-hidden
        style={{ y: yImgB }}
        className="float-slow pointer-events-none absolute bottom-[12%] right-[20%] hidden w-32 -rotate-6 rounded-2xl border border-[hsl(var(--border))] object-cover opacity-80 shadow-2xl xl:block"
      />

      <motion.div style={{ opacity }} className="relative z-10 mx-auto w-full max-w-[1400px]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mb-8 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-[hsl(var(--primary))]" />
          <span className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--muted))]">
            Creative Studio · Serving Globally since 2015
          </span>
        </motion.div>

        <motion.h1
          style={{ y: yWord }}
          className="font-logo text-[17vw] font-black italic leading-[0.82] tracking-tight text-glow md:text-[15vw] lg:text-[13rem]"
        >
          <MaskLines
            animateOnLoad
            delay={0.35}
            lines={["Design", <span key="a" className="text-[hsl(var(--primary))]">4you</span>]}
          />
        </motion.h1>

        <div className="mt-10 grid gap-10 md:grid-cols-[1.1fr_0.9fr] md:items-end">
          <MaskLines
            animateOnLoad
            delay={0.7}
            className="font-head text-2xl font-medium leading-tight sm:text-3xl md:text-4xl"
            lines={[
              "Premium branding, cinematic 3D",
              "& performance marketing —",
              <span key="l" className="text-[hsl(var(--muted))]">built to be everyone&apos;s first choice.</span>,
            ]}
          />

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="flex flex-col items-start gap-4"
          >
            <p className="max-w-sm text-base text-[hsl(var(--muted))]">
              From India to the world — a full-service studio for brands that refuse to look ordinary. Your first 30-min consultation is on us.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href={WHATSAPP}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-book-btn"
                className="group flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-7 py-4 text-base font-semibold text-[hsl(var(--primary-fg))] transition-transform duration-300 hover:scale-105"
              >
                <MessageCircle size={18} /> Book a Free Call
                <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <button
                onClick={openBookCall}
                data-testid="hero-calendly-btn"
                className="rounded-full border border-[hsl(var(--border))] px-7 py-4 text-base font-medium text-[hsl(var(--fg))] transition-colors duration-300 hover:bg-[hsl(var(--fg)/0.06)]"
              >
                Schedule Discovery Call
              </button>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2 text-[hsl(var(--muted))]"
      >
        <span className="text-[10px] uppercase tracking-[0.25em]">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.6, repeat: Infinity }}>
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
};
